import { useState } from "react";
import Layout from "@/components/Layout";
import { Sparkles, Copy, Check, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const topicSuggestions = [
  "Naklejki 3D na samochody – jak wybrać?",
  "Jak dbać o naklejki zalewane żywicą?",
  "Naklejki 3D vs druk UV – porównanie",
  "Trendy w brandingu produktowym 2025",
  "Naklejki holograficzne – zastosowania",
  "Jak zamówić naklejki 3D online?",
];

const AiGenerator = () => {
  const [topic, setTopic] = useState("");
  const [result, setResult] = useState<{ title: string; description: string; content: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const generate = async () => {
    if (!topic.trim()) return;
    setLoading(true);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke("generate-blog-article", {
        body: { topic: topic.trim() },
      });

      if (error) throw error;
      setResult(data);
    } catch (err: any) {
      toast({ title: "Błąd", description: err.message || "Nie udało się wygenerować artykułu.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (!result) return;
    const text = `# ${result.title}\n\nMeta description: ${result.description}\n\n${result.content}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({ title: "Skopiowano!", description: "Artykuł został skopiowany do schowka." });
  };

  return (
    <Layout>
      <section className="gradient-navy py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl lg:text-4xl font-bold text-primary-foreground mb-2">
            <Sparkles className="inline w-8 h-8 mr-2 text-accent" />
            AI Generator Artykułów
          </h1>
          <p className="text-primary-foreground/70">Generuj artykuły SEO na tematy związane z naklejkami 3D</p>
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-4 max-w-3xl">
          {/* Topic input */}
          <div className="bg-card border border-border rounded-xl p-6 mb-6">
            <label className="block text-sm font-semibold text-foreground mb-2">Temat artykułu</label>
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="np. Jak wybrać naklejki 3D na samochód?"
              className="w-full bg-background border border-input rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent mb-4"
              onKeyDown={(e) => e.key === "Enter" && generate()}
            />
            <button
              onClick={generate}
              disabled={loading || !topic.trim()}
              className="gradient-gold text-accent-foreground font-semibold px-6 py-3 rounded-lg hover:opacity-90 transition-opacity shadow-gold disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {loading ? "Generuję..." : "Generuj artykuł"}
            </button>
          </div>

          {/* Suggestions */}
          <div className="mb-8">
            <p className="text-xs text-muted-foreground mb-2">Propozycje tematów:</p>
            <div className="flex flex-wrap gap-2">
              {topicSuggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => setTopic(s)}
                  className="text-xs px-3 py-1.5 rounded-full border border-border bg-secondary hover:border-accent/50 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Result */}
          {result && (
            <div className="bg-card border border-border rounded-xl p-6 animate-fade-in">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-foreground">{result.title}</h2>
                <button onClick={copyToClipboard} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  {copied ? "Skopiowano" : "Kopiuj"}
                </button>
              </div>
              <div className="bg-secondary/50 rounded-lg p-3 mb-4">
                <p className="text-xs text-muted-foreground"><strong>Meta description:</strong> {result.description}</p>
              </div>
              <div className="prose prose-sm max-w-none text-muted-foreground whitespace-pre-wrap leading-relaxed">
                {result.content}
              </div>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default AiGenerator;
